import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use relative URL in production, env variable in development
const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Key for tracking location prompt
const LOCATION_PROMPTED_KEY = 'location_permission_prompted';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // iOS Safari private browsing fix - wrap in try-catch
    try {
      return localStorage.getItem('token');
    } catch (e) {
      console.warn('localStorage not available');
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Safe localStorage helper for iOS
  const safeSetStorage = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('localStorage not available, using memory storage');
    }
  };

  const safeRemoveStorage = (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage not available');
    }
  };

  // Check if location prompt should be shown
  const checkLocationPrompt = () => {
    try {
      const prompted = localStorage.getItem(LOCATION_PROMPTED_KEY);
      if (!prompted) {
        // Small delay to let the UI settle after login/register
        setTimeout(() => setShowLocationPrompt(true), 1000);
      }
    } catch (e) {
      console.warn('Could not check location prompt status');
    }
  };

  // Handle location prompt close
  const closeLocationPrompt = () => {
    setShowLocationPrompt(false);
  };

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth status on mount
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      // Not authenticated
      setUser(null);
      setToken(null);
      safeRemoveStorage('token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token, checkAuth]);

  const login = async (credentials) => {
    // Build login payload based on login type
    const payload = {};
    
    if (credentials.email) {
      // Admin login with email
      payload.email = credentials.email;
    } else if (credentials.phone) {
      // Regular user login with phone
      payload.phone = credentials.phone;
      payload.country_code = credentials.countryCode || '+234';
    }
    
    payload.password = credentials.password;
    if (credentials.totpCode) {
      payload.totp_code = credentials.totpCode;
    }
    
    const response = await axios.post(`${API_URL}/api/auth/login`, payload);
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, user_id: response.data.user_id };
    }
    
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    safeSetStorage('token', newToken);
    
    // Trigger location prompt after successful login
    checkLocationPrompt();
    
    return userData;
  };

  const register = async (userData) => {
    let response;
    if (userData.signupMethod === 'email') {
      response = await axios.post(`${API_URL}/api/auth/email/register`, {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        role: userData.role || 'attendee',
        verification_token: userData.verificationToken
      });
    } else {
      response = await axios.post(`${API_URL}/api/auth/register`, {
        name: userData.name,
        phone: userData.phone,
        country_code: userData.countryCode || '+234',
        email: userData.email || null,
        password: userData.password,
        role: userData.role || 'attendee',
        verification_token: userData.verificationToken || null
      });
    }
    const { token: newToken, user: userResponse } = response.data;
    setToken(newToken);
    setUser(userResponse);
    safeSetStorage('token', newToken);
    
    // Trigger location prompt after successful registration
    checkLocationPrompt();
    
    return userResponse;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/session`,
      { session_id: sessionId }
    );
    const { token: newToken, user: userData } = response.data;
    if (newToken) {
      setToken(newToken);
      safeSetStorage('token', newToken);
    }
    setUser(userData || response.data);
    return userData || response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
    safeRemoveStorage('token');
  };

  const upgradeToOrganizer = async () => {
    // Explicitly pass role=organizer to upgrade from attendee to organizer
    const response = await axios.put(`${API_URL}/api/auth/role?role=organizer`, {});
    setUser(prev => ({ ...prev, role: response.data.role }));
    return response.data;
  };

  // Helper to get appropriate dashboard URL based on admin role
  const getAdminDashboardUrl = (adminRole) => {
    switch (adminRole) {
      case 'super_admin':
        return '/admin';
      case 'finance':
        return '/admin/analytics';
      case 'support':
        return '/admin/support';
      case 'moderator':
        return '/admin/moderator';
      default:
        return '/dashboard';
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isOrganizer: user?.role === 'organizer' || (user?.role === 'admin' && user?.admin_role === 'super_admin'),
    isAdmin: user?.role === 'admin',
    adminRole: user?.admin_role,
    has2FAEnabled: user?.two_fa_enabled,
    showLocationPrompt,
    closeLocationPrompt,
    login,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    upgradeToOrganizer,
    refreshUser: checkAuth,
    getAdminDashboardUrl
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
